import Axios from "axios";

declare module "axios" {
  interface AxiosRequestConfig {
    systemOneApiConfig?: SystemOneApiConfig;
  }
}

interface SystemOneApiConfig {
  toastOnError: boolean;
}

const endpoint = <F extends (...args: any[]) => any, U extends (...args: any[]) => string>(
  fn: F,
  absoluteUrl: U,
  requestSizeLimit = 3145728
) => {
  return Object.assign(fn, { absoluteUrl, requestSizeLimit });
}

const api = Axios.create({ withCredentials: true });

const createApiFunctions = (baseURL: string, acceptLanguageValue?: string) => {
  api.defaults.baseURL = baseURL;
  if (acceptLanguageValue != null) {
      api.defaults.headers.common["Accept-Language"] = acceptLanguageValue;
  }

  const apiFunctions = {
    AdminOne: {
      MigrateRecipients: endpoint(
        (systemOneApiConfig?: SystemOneApiConfig) => api.get<string>(`/api/v1/adminone/MigrateRecipients`, { systemOneApiConfig }),
        () => `${baseURL}/api/v1/adminone/MigrateRecipients`,
        3145728
      ),
      CreateShowToDateRecords: endpoint(
        (truncateExisting: boolean, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<string>>(`/api/v1/adminone/createshowtodaterecords`, undefined, { params: { truncateExisting }, systemOneApiConfig }),
        (truncateExisting: boolean) => `${baseURL}/api/v1/adminone/createshowtodaterecords?truncateExisting=${truncateExisting}`,
        3145728
      ),
      RefreshExternalIdentities: endpoint(
        (modifiedLongerThanHours: number, systemOneApiConfig?: SystemOneApiConfig) => api.post<string>(`/api/v1/adminone/updateexternalidentities`, undefined, { params: { modifiedLongerThanHours }, systemOneApiConfig }),
        (modifiedLongerThanHours: number) => `${baseURL}/api/v1/adminone/updateexternalidentities?modifiedLongerThanHours=${modifiedLongerThanHours}`,
        3145728
      )
    },
    Artists: {
      GetArtistsForCurrentUserPaginated: endpoint(
        (excludeIds: number[], keyword: string, page: number, per_page: number, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapper<server.dto.LegacyArtist[]>>(`/api/v1/artists/myartists`, excludeIds, { params: { keyword, page, per_page }, systemOneApiConfig }),
        (keyword: string, page: number, per_page: number) => `${baseURL}/api/v1/artists/myartists?keyword=${keyword}&page=${page}&per_page=${per_page}`,
        3145728
      ),
      ListAppointments: endpoint(
        (artistId: number, timeMin: Date, timeMax: Date, page: number, per_page: number, appointmentTypes: server.dto.AppointmentTypes[], systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<any[]>>(`/api/v1/artists/${artistId}/appointments`, { params: { timeMin, timeMax, page, per_page, appointmentTypes }, systemOneApiConfig }),
        (artistId: number, timeMin: Date, timeMax: Date, page: number, per_page: number, appointmentTypes: server.dto.AppointmentTypes[]) => `${baseURL}/api/v1/artists/${artistId}/appointments?timeMin=${timeMin}&timeMax=${timeMax}&page=${page}&per_page=${per_page}&appointmentTypes=${appointmentTypes}`,
        3145728
      )
    },
    Companies: {
      CachePointOfInterestMarkers: endpoint(
        (companyId: number, markers: server.dto.AddressMarker[], systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/api/v1/companies/${companyId}/markers`, markers, { systemOneApiConfig }),
        (companyId: number) => `${baseURL}/api/v1/companies/${companyId}/markers`,
        3145728
      ),
      GetPointOfInterestMarkers: endpoint(
        (companyId: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.AddressMarker[]>(`/api/v1/companies/${companyId}/markers`, { systemOneApiConfig }),
        (companyId: number) => `${baseURL}/api/v1/companies/${companyId}/markers`,
        3145728
      ),
      GetNearbyAirports: endpoint(
        (companyId: number, radius: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.Airport[]>(`/api/v1/companies/${companyId}/nearbyAirports`, { params: { radius }, systemOneApiConfig }),
        (companyId: number, radius: number) => `${baseURL}/api/v1/companies/${companyId}/nearbyAirports?radius=${radius}`,
        3145728
      ),
      GetLegacyDetails: endpoint(
        (companyId: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.LegacyCompany>(`/api/v1/companies/${companyId}`, { systemOneApiConfig }),
        (companyId: number) => `${baseURL}/api/v1/companies/${companyId}`,
        3145728
      ),
      FilesGet: endpoint(
        (companyId: number, page: number, per_page: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.LegacyFile[]>>(`/api/v1/companies/${companyId}/files`, { params: { page, per_page }, systemOneApiConfig }),
        (companyId: number, page: number, per_page: number) => `${baseURL}/api/v1/companies/${companyId}/files?page=${page}&per_page=${per_page}`,
        3145728
      )
    },
    Contacts: {
      GetDetails: endpoint(
        (contactId: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.LegacyContactSmall>(`/api/v1/contacts/${contactId}`, { systemOneApiConfig }),
        (contactId: number) => `${baseURL}/api/v1/contacts/${contactId}`,
        3145728
      ),
      FilesGet: endpoint(
        (contactId: number, page: number, per_page: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.LegacyFile[]>>(`/api/v1/contacts/${contactId}/files`, { params: { page, per_page }, systemOneApiConfig }),
        (contactId: number, page: number, per_page: number) => `${baseURL}/api/v1/contacts/${contactId}/files?page=${page}&per_page=${per_page}`,
        3145728
      )
    },
    ExternalCalendars: {
      GetExternalEvent: endpoint(
        (externalCalendarId: number, externalEventId: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<any>(`/api/v1/externalcalendars/${externalCalendarId}/events/${externalEventId}`, { systemOneApiConfig }),
        (externalCalendarId: number, externalEventId: number) => `${baseURL}/api/v1/externalcalendars/${externalCalendarId}/events/${externalEventId}`,
        3145728
      )
    },
    ExternalEventsources: {
      GetExternalEvent: endpoint(
        (externalCalendarId: number, externalEventId: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<any>(`/api/v1/externaleventsources/${externalCalendarId}/events/${externalEventId}`, { systemOneApiConfig }),
        (externalCalendarId: number, externalEventId: number) => `${baseURL}/api/v1/externaleventsources/${externalCalendarId}/events/${externalEventId}`,
        3145728
      )
    },
    Files: {
      DownloadClientFile: endpoint(
        (fileId: string, fileName: string, systemOneApiConfig?: SystemOneApiConfig) => api.get<any>(`/api/v1/files/clientfile/${fileId}/download/${fileName}`, { systemOneApiConfig }),
        (fileId: string, fileName: string) => `${baseURL}/api/v1/files/clientfile/${fileId}/download/${fileName}`,
        3145728
      ),
      DownloadFlightFile: endpoint(
        (fileId: string, fileName: string, systemOneApiConfig?: SystemOneApiConfig) => api.get<any>(`/api/v1/files/flight/${fileId}/download/${fileName}`, { systemOneApiConfig }),
        (fileId: string, fileName: string) => `${baseURL}/api/v1/files/flight/${fileId}/download/${fileName}`,
        3145728
      ),
      DownloadTrainFile: endpoint(
        (fileId: string, fileName: string, systemOneApiConfig?: SystemOneApiConfig) => api.get<any>(`/api/v1/files/train/${fileId}/download/${fileName}`, { systemOneApiConfig }),
        (fileId: string, fileName: string) => `${baseURL}/api/v1/files/train/${fileId}/download/${fileName}`,
        3145728
      )
    },
    Flights: {
      GetLegacySuperFlight: endpoint(
        (flightId: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<any>(`/api/v1/flights/${flightId}`, { systemOneApiConfig }),
        (flightId: number) => `${baseURL}/api/v1/flights/${flightId}`,
        3145728
      ),
      ListFlightFiles: endpoint(
        (flightId: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.LegacyFile[]>(`/api/v1/flights/${flightId}/files`, { systemOneApiConfig }),
        (flightId: number) => `${baseURL}/api/v1/flights/${flightId}/files`,
        3145728
      )
    },
    GroundTransports: {
      GetLegacyGroundTransport: endpoint(
        (groundTransportId: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<any>(`/api/v1/groundtransports/${groundTransportId}`, { systemOneApiConfig }),
        (groundTransportId: number) => `${baseURL}/api/v1/groundtransports/${groundTransportId}`,
        3145728
      )
    },
    Guestlist: {
      GetGuestlistByShow: endpoint(
        (showId: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapperWithRequest<server.dto.LegacyGuestlist, Object>>(`/api/v1/guestlist/${showId}`, { systemOneApiConfig }),
        (showId: number) => `${baseURL}/api/v1/guestlist/${showId}`,
        3145728
      ),
      UpdateGuestlist: endpoint(
        (dto: server.dto.LegacyGuestlist, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapperWithRequest<string, Object>>(`/api/v1/guestlist`, dto, { systemOneApiConfig }),
        () => `${baseURL}/api/v1/guestlist`,
        3145728
      ),
      AddGuestlist: endpoint(
        (showId: number, systemOneApiConfig?: SystemOneApiConfig) => api.put<server.dto.ApiResponseWrapperWithRequest<string, Object>>(`/api/v1/guestlist/${showId}`, undefined, { systemOneApiConfig }),
        (showId: number) => `${baseURL}/api/v1/guestlist/${showId}`,
        3145728
      ),
      DeleteGuestlist: endpoint(
        (guestlistId: number, systemOneApiConfig?: SystemOneApiConfig) => api.delete(`/api/v1/guestlist/${guestlistId}`, { systemOneApiConfig }),
        (guestlistId: number) => `${baseURL}/api/v1/guestlist/${guestlistId}`,
        3145728
      ),
      AddGuestlistPlace: endpoint(
        (dto: server.dto.LegacyGuestlistPlace, guestlistId: number, systemOneApiConfig?: SystemOneApiConfig) => api.put<server.dto.ApiResponseWrapperWithRequest<string, Object>>(`/api/v1/guestlist/place`, dto, { params: { guestlistId }, systemOneApiConfig }),
        (guestlistId: number) => `${baseURL}/api/v1/guestlist/place?guestlistId=${guestlistId}`,
        3145728
      ),
      LegacyRemoveGuestlistPlace: endpoint(
        (placeId: number, systemOneApiConfig?: SystemOneApiConfig) => api.delete(`/api/v1/guestlist/place`, { systemOneApiConfig }),
        () => `${baseURL}/api/v1/guestlist/place`,
        3145728
      ),
      RemoveGuestlistPlace: endpoint(
        (placeId: number, systemOneApiConfig?: SystemOneApiConfig) => api.delete(`/api/v1/guestlist/place/${placeId}`, { systemOneApiConfig }),
        (placeId: number) => `${baseURL}/api/v1/guestlist/place/${placeId}`,
        3145728
      ),
      UpdateGuestlistPlace: endpoint(
        (dto: server.dto.LegacyGuestlistPlace, systemOneApiConfig?: SystemOneApiConfig) => api.post<any>(`/api/v1/guestlist/place`, dto, { systemOneApiConfig }),
        () => `${baseURL}/api/v1/guestlist/place`,
        3145728
      ),
      AddGuest: endpoint(
        (dto: server.dto.LegacyGuest, placeId: number, systemOneApiConfig?: SystemOneApiConfig) => api.put<server.dto.ApiResponseWrapperWithRequest<string, Object>>(`/api/v1/guestlist/place/${placeId}/guest`, dto, { systemOneApiConfig }),
        (placeId: number) => `${baseURL}/api/v1/guestlist/place/${placeId}/guest`,
        3145728
      ),
      LegacyRemoveGuest: endpoint(
        (guestId: number, systemOneApiConfig?: SystemOneApiConfig) => api.delete(`/api/v1/guestlist/place/guest`, { systemOneApiConfig }),
        () => `${baseURL}/api/v1/guestlist/place/guest`,
        3145728
      ),
      RemoveGuest: endpoint(
        (guestId: number, systemOneApiConfig?: SystemOneApiConfig) => api.delete(`/api/v1/guestlist/place/guest/${guestId}`, { systemOneApiConfig }),
        (guestId: number) => `${baseURL}/api/v1/guestlist/place/guest/${guestId}`,
        3145728
      ),
      UpdateGuest: endpoint(
        (dto: server.dto.LegacyGuest, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponseWrapperWithRequest<string, Object>>(`/api/v1/guestlist/place/guest`, dto, { systemOneApiConfig }),
        () => `${baseURL}/api/v1/guestlist/place/guest`,
        3145728
      )
    },
    Notifications: {
      GetNotifications: endpoint(
        (forMobile: boolean, page: number, per_page: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.Notification[]>>(`/api/v1/notifications`, { params: { forMobile, page, per_page }, systemOneApiConfig }),
        (forMobile: boolean, page: number, per_page: number) => `${baseURL}/api/v1/notifications?forMobile=${forMobile}&page=${page}&per_page=${per_page}`,
        3145728
      ),
      MarkRead: endpoint(
        (notifications: number[], read: boolean, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/api/v1/notifications/read/${read}`, notifications, { systemOneApiConfig }),
        (read: boolean) => `${baseURL}/api/v1/notifications/read/${read}`,
        3145728
      ),
      MarkAllSeen: endpoint(
        (forMobile: boolean, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.ApiResponse>(`/api/v1/notifications/allseen`, undefined, { params: { forMobile }, systemOneApiConfig }),
        (forMobile: boolean) => `${baseURL}/api/v1/notifications/allseen?forMobile=${forMobile}`,
        3145728
      )
    },
    Promoters: {
      GetDetails: endpoint(
        (promoterId: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.LegacyPromoter>(`/api/v1/promoters/${promoterId}`, { systemOneApiConfig }),
        (promoterId: number) => `${baseURL}/api/v1/promoters/${promoterId}`,
        3145728
      )
    },
    Register: {
      DeleteByPns: endpoint(
        (pns: any, systemOneApiConfig?: SystemOneApiConfig) => api.delete(`/api/v1/register/pns`, { systemOneApiConfig }),
        () => `${baseURL}/api/v1/register/pns`,
        3145728
      )
    },
    Shows: {
      GetLegacyShow: endpoint(
        (showId: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<any>(`/api/v1/shows/${showId}`, { systemOneApiConfig }),
        (showId: number) => `${baseURL}/api/v1/shows/${showId}`,
        3145728
      ),
      FilesGet: endpoint(
        (showId: number, page: number, per_page: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponseWrapper<server.dto.LegacyFile[]>>(`/api/v1/shows/${showId}/files`, { params: { page, per_page }, systemOneApiConfig }),
        (showId: number, page: number, per_page: number) => `${baseURL}/api/v1/shows/${showId}/files?page=${page}&per_page=${per_page}`,
        3145728
      )
    },
    Tests: {
      HelloWorld: endpoint(
        (systemOneApiConfig?: SystemOneApiConfig) => api.get<string>(`/api/v1/tests/hi`, { systemOneApiConfig }),
        () => `${baseURL}/api/v1/tests/hi`,
        3145728
      ),
      TestException: endpoint(
        (systemOneApiConfig?: SystemOneApiConfig) => api.get<string>(`/api/v1/tests/exception/sync`, { systemOneApiConfig }),
        () => `${baseURL}/api/v1/tests/exception/sync`,
        3145728
      ),
      TestExceptionAsync: endpoint(
        (systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.ApiResponse>(`/api/v1/tests/exception/async`, { systemOneApiConfig }),
        () => `${baseURL}/api/v1/tests/exception/async`,
        3145728
      ),
      TestExceptionInMediator: endpoint(
        (systemOneApiConfig?: SystemOneApiConfig) => api.get<string>(`/api/v1/tests/exception/mediator`, { systemOneApiConfig }),
        () => `${baseURL}/api/v1/tests/exception/mediator`,
        3145728
      )
    },
    Trains: {
      ListTrainFiles: endpoint(
        (trainId: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<server.dto.LegacyFile[]>(`/api/v1/trains/${trainId}/files`, { systemOneApiConfig }),
        (trainId: number) => `${baseURL}/api/v1/trains/${trainId}/files`,
        3145728
      ),
      GetTrainSegment: endpoint(
        (trainId: number, trainSegmentId: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<any>(`/api/v1/trains/${trainId}/segments/${trainSegmentId}`, { systemOneApiConfig }),
        (trainId: number, trainSegmentId: number) => `${baseURL}/api/v1/trains/${trainId}/segments/${trainSegmentId}`,
        3145728
      )
    },
    Users: {
      GetCurrentUser: endpoint(
        (systemOneApiConfig?: SystemOneApiConfig) => api.get<any>(`/api/v1/users/user`, { systemOneApiConfig }),
        () => `${baseURL}/api/v1/users/user`,
        3145728
      ),
      ConnectSocial: endpoint(
        (issuer: string, token: string, systemOneApiConfig?: SystemOneApiConfig) => api.get<any>(`/api/v1/users/user/sociallogin`, { params: { issuer, token }, systemOneApiConfig }),
        (issuer: string, token: string) => `${baseURL}/api/v1/users/user/sociallogin?issuer=${issuer}&token=${token}`,
        3145728
      ),
      UpdateAvatar: endpoint(
        (userId: number, systemOneApiConfig?: SystemOneApiConfig) => api.post<server.dto.FileUploadResult>(`/api/v1/users/${userId}/avatar`, undefined, { systemOneApiConfig }),
        (userId: number) => `${baseURL}/api/v1/users/${userId}/avatar`,
        3145728
      )
    },
    Venues: {
      GetDetails: endpoint(
        (venueId: number, systemOneApiConfig?: SystemOneApiConfig) => api.get<any>(`/api/v1/venues/${venueId}`, { systemOneApiConfig }),
        (venueId: number) => `${baseURL}/api/v1/venues/${venueId}`,
        3145728
      )
    },

  }

  return apiFunctions;
}

export { createApiFunctions, api };
